import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button, Card } from '@/components/ui';

// Last-line-of-defence boundary so a thrown render error never white-screens
// the operator mid-hearing. Reset clears the error and forces a re-mount of
// the children subtree; if the same error reappears, the operator sees the
// same fallback again rather than crashing.

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Phase 7 will replace this with Sentry. For now, log enough that the
    // browser console captures the trace and the component path.
    console.error('Court Copilot render error:', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  reload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <main className="grid min-h-screen place-items-center bg-stone-950 px-5 py-10">
          <Card variant="panel" padding="lg" className="w-full max-w-md space-y-4">
            <div className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" aria-hidden />
              <p className="font-display text-xl italic">Court Copilot hit an error</p>
            </div>
            <p className="text-sm text-stone-300">
              The view crashed but your transcript and session are saved on the server. Reset to try
              again, or reload the page to start over.
            </p>
            <pre className="overflow-x-auto rounded border border-stone-800 bg-stone-950 p-3 text-[11px] text-stone-400">
              {this.state.error.message}
            </pre>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={this.reset}
                leftIcon={<RefreshCw className="h-4 w-4" />}
              >
                Reset view
              </Button>
              <Button variant="secondary" onClick={this.reload}>
                Reload page
              </Button>
            </div>
          </Card>
        </main>
      );
    }
    return this.props.children;
  }
}
